import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Timeline from '../../../../../../../core/components/atoms/Timeline';
import Player from '../../../../../../../core/components/molecules/Player';
import PlayerAprova from '../../../../../../../core/components/atoms/PlayerAprova';
import { PlayerType } from '../../../../../../../core/models/video';
import { useBuildPlayer } from '../../../../../hooks/DailySchedule/useBuildPlayer';
import { useGetLessonDetail } from '../../../../../hooks/DailySchedule/useGetLessonDetail';
import { useDoneLesson } from '../../../../../hooks/DailySchedule/useDoneLesson';
import { getDoneFromPAResponse } from '../../../../../utils/contentDone';
import { IPALessonDto } from '../../../../../model/dailySchedule';
import { useDailyScheduleContentStore } from '../../../../../stores/useDailyScheduleContent';
import { Button } from '../../../../../../../core/components/atoms/Button';
import { PlayerQuiz } from '../../../../../../../core/components/organism/PlayerQuiz';
import { useFetchLessonQuiz } from '../../../../../../../core/hooks/useFetchLessonQuiz';
import { useUserStore } from '../../../../../../Auth/stores/authStore';
import { useLessonProgressLog } from '../../../../../../../core/hooks/useLessonProgressLog';

interface IPlayerSectionProps {
  lesson: IPALessonDto;
}

function PlayerSection({ lesson }: IPlayerSectionProps) {
  if (!lesson) {
    return null;
  }

  const { token, player, timeline } = useBuildPlayer(lesson);

  // captionUri e embedUrl não vêm no IPALessonDto da agenda do dia; só no
  // detalhe da aula (/lessons/:slug).
  const {
    data: lessonDetail,
    isPending: isLessonDetailPending,
    isError: isLessonDetailError,
  } = useGetLessonDetail(lesson.slug);

  // Decisão novo vs. legado idêntica à do LessonPlayer: prioriza o player novo
  // (embedUrl); na falta dele, usa o legado (vimeo/sambatech). Esperamos o
  // detalhe resolver antes de decidir para não trocar o player em tela (pop-in).
  const embedUrl = lessonDetail?.embedUrl;
  const isPlayerResolved = !isLessonDetailPending || isLessonDetailError;

  // Log de progresso: o player informa os segundos, o backend decide se a aula
  // foi iniciada ou consumida.
  const reportarProgresso = useLessonProgressLog({
    slug: lesson.slug,
    origin: 'custom_schedule',
  });

  const [seekTo, setSeekTo] = useState<{
    seconds: number;
    requestId: number;
  }>();

  const enableNewFeatures26 = useUserStore((s) => s.user?.enableNewFeatures26);
  const [isQuizOpen, setIsQuizOpen] = useState<boolean>(false);
  const { data: quiz } = useFetchLessonQuiz(lesson.id, true);
  const quizRef = useRef(quiz);

  useEffect(() => {
    quizRef.current = quiz;
  }, [quiz]);

  const handleChangeTimeline = (time: {
    seconds: number;
    id: string;
    text: string;
  }) => {
    const seconds = Number(time?.seconds);
    if (!Number.isFinite(seconds)) return;
    // O requestId garante o salto mesmo ao clicar duas vezes no mesmo item.
    setSeekTo((prev) => ({
      seconds,
      requestId: (prev?.requestId ?? 0) + 1,
    }));
  };

  const { scheduleId } = useParams();
  const { mutateAsync: onDoneLesson } = useDoneLesson();
  const { contentList, setContentList } = useDailyScheduleContentStore((s) => ({
    contentList: s.contentList,
    setContentList: s.setContentList,
  }));

  async function handleDoneLesson() {
    if (!scheduleId) return;

    // A rota /concluir é toggle: se a aula já está marcada como assistida,
    // disparar de novo (ex.: ao rever e ultrapassar a marca virtual) reverteria
    // o status. Então não faz nada quando já está concluída.
    const alreadyDone = contentList.find(
      (item) => item.content.id === lesson.id
    )?.content.done;
    if (alreadyDone) return;

    const response = await onDoneLesson({
      contentId: lesson.id,
      cronogramaId: scheduleId,
    });

    const done = getDoneFromPAResponse(response);

    const newContentList = contentList.map((item) => {
      if (item.content.id === lesson.id) {
        return { ...item, content: { ...item.content, done } };
      }
      return item;
    });
    setContentList(newContentList);
  }

  return (
    <div>
      <div
        className='relative flex h-[500px] w-full flex-col lg:flex-row'
        id='lesson-player-container'
      >
        {quiz &&
        quiz.exercises.length > 0 &&
        !isQuizOpen &&
        enableNewFeatures26 === true ? (
          <Button
            variant='contained'
            LeftIcon={'quiz'}
            onClick={() => setIsQuizOpen(true)}
            negative
            className='group absolute right-5 top-5 z-[3]'
          >
            <span className='hidden group-hover:block'>
              Responda ao quiz da aula
            </span>
          </Button>
        ) : null}

        {quiz && isQuizOpen && enableNewFeatures26 === true ? (
          <PlayerQuiz
            quiz={quiz}
            isOpen={isQuizOpen}
            setIsOpen={setIsQuizOpen}
          />
        ) : !isPlayerResolved ? (
          <div
            className='h-full w-full animate-pulse bg-black'
            aria-busy
            aria-label='Carregando player'
          />
        ) : embedUrl ? (
          <PlayerAprova
            embedUrl={embedUrl}
            title={lesson.title}
            className='h-full w-full'
          />
        ) : (
          <Player
            contentId={lesson.id}
            playerType={player as PlayerType}
            token={token}
            initialTime={0}
            seekTo={seekTo}
            className='h-full w-full'
            onFinish={handleDoneLesson}
            onProgressSeconds={reportarProgresso}
            captionUri={lessonDetail?.captionUri}
          />
        )}
      </div>
      <Timeline
        items={timeline}
        label='Tópicos da aula'
        onClickItem={handleChangeTimeline}
      />

      {quiz && quiz.exercises.length > 0 && enableNewFeatures26 === true ? (
        <div className='flex justify-center pt-8 md:hidden'>
          <Button
            variant='outlined'
            LeftIcon={'quiz'}
            onClick={() => setIsQuizOpen(true)}
            full
          >
            Responda ao quiz da aula
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default PlayerSection;
